/**
 * Feature Flags
 *
 * Switches features on or off in navigation and settings — for everyone.
 *
 * Until 2026-09-11 admins bypassed every flag and employees saw only what was
 * switched on. Admin and employee now share one set of rights, so a flag no
 * longer asks for a role: what is on, the whole office sees. `mail` and
 * `administration` went from `false` to `true` at that point, which is
 * exactly what admins saw before.
 */

/** Feature flags — switch off only what nobody should see yet. */
const FEATURE_FLAGS = {
  careers: true,
  mail: true,
  // Verwaltungsreiter (Unternehmen, Protokoll, Papierkorb). Standen bis zum
  // CRM-Ausbau am `pipeline`-Flag und waren dadurch faktisch Admin-only.
  administration: true,
  educators: true,
  schools: true,
  // Interim school-master-data queue (S1) — visible to the people meant to
  // work through it: the route already admits admin/employee (routes.tsx)
  // and firestore.rules:278-280 already permits their writes, so this flag
  // only gates nav discoverability, not access. See SchulstammdatenPage's
  // header comment.
  schoolMasterData: true,
} as const;

export type FeatureKey = keyof typeof FEATURE_FLAGS;

/** Check if a feature is switched on. */
export function isFeatureEnabled(key: FeatureKey): boolean {
  return FEATURE_FLAGS[key];
}

/** Feature-to-route mapping for landing page resolution */
const FEATURE_ROUTES: Partial<Record<FeatureKey, string>> = {
  schools: '/schools',
  educators: '/lehrkraefte',
  careers: '/careers',
};

/** Landing page priority per role (first enabled feature wins) */
const LANDING_PRIORITY: FeatureKey[] = ['schools', 'educators', 'careers'];

/**
 * Get the default landing route for a role after login.
 * Returns the first feature-flagged route that is enabled for the role.
 */
export function getDefaultRoute(role: string): string {
  if (role === 'educator') return '/e';

  for (const key of LANDING_PRIORITY) {
    if (isFeatureEnabled(key) && FEATURE_ROUTES[key]) {
      return FEATURE_ROUTES[key];
    }
  }

  // Fallback: first feature that has a route defined (regardless of flag)
  const fallbackKey = LANDING_PRIORITY.find((k) => FEATURE_ROUTES[k]);
  return FEATURE_ROUTES[fallbackKey!] ?? '/login';
}
